import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';

export interface ConnectivityResult
{
    reachable: boolean;
    latencyMs: number;
    quality: 'good' | 'slow' | 'unreachable';
}

@Injectable()
export class HealthService
{
    private endPoint = '/health';
    private http = inject(HttpClient);

    checkHealth(): Promise<boolean>
    {
        return firstValueFrom(this.http.get<boolean>(this.endPoint));
    }

    async checkConnectivity(timeoutMs = 5000): Promise<ConnectivityResult>
    {
        const measure = async (): Promise<number | null> =>
        {
            const start = Date.now();

            try
            {
                const url = `${this.endPoint}/ping`;

                await firstValueFrom(
                    this.http.get(url, { responseType: 'text' }).pipe(timeout(timeoutMs))
                );

                return Date.now() - start;
            }
            catch
            {
                return null;
            }
        };

        // Two consecutive requests — catches flaky connections
        const [first, second] = await Promise.all([measure(), measure()]);

        if (first === null && second === null)
        {
            return { reachable: false, latencyMs: timeoutMs, quality: 'unreachable' };
        }

        const samples = [first, second].filter((v): v is number => v !== null);
        const avg = samples.reduce((a, b) => a + b, 0) / samples.length;

        return {
            reachable: true,
            latencyMs: Math.round(avg),
            quality: avg > 3000 ? 'slow' : 'good',
        };
    }
}
